import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch } from '../../redux/store';
import { DBDocumentThunks } from '../../redux/thunks/DBServiceThunks';
import { processDocument } from '../../common/backend-api/backend-api';
import { getLanguageDirection } from '../../i18n/config';
import { GoogleDriveService } from '../../services/google-drive-service';
import GoogleDriveIcon from '../icons/GoogleDriveIcon';

import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription
} from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Upload, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { CheckCircle, AlertCircle, Info, X } from 'lucide-react';

// Constants
const MAX_FILE_SIZE_MB = 10;

interface DocumentUploadDialogProps {
    open: boolean;
    onClose: () => void;
    caseId?: string;
    knowledgebaseId?: string;
}

const DocumentUploadDialog: React.FC<DocumentUploadDialogProps> = ({
    open,
    onClose,
    caseId,
    knowledgebaseId
}) => {
    const { t, i18n } = useTranslation();
    const isRTL = getLanguageDirection(i18n.language) === 'rtl';
    const dispatch = useAppDispatch();

    const [uploadProgress, setUploadProgress] = useState(0);
    const [isDragging, setIsDragging] = useState(false);
    const [isApiLoaded, setIsApiLoaded] = useState(false);
    const snackbarTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => () => { if (snackbarTimerRef.current) clearTimeout(snackbarTimerRef.current); }, []);

    // Initialize Google Drive Service
    useEffect(() => {
        const initDrive = async () => {
            try {
                await GoogleDriveService.getInstance().initialize();
                setIsApiLoaded(true);
            } catch (error) {
                console.error('Failed to initialize Google Drive:', error);
            }
        };
        initDrive();
    }, []);

    const openPicker = async () => {
        return await GoogleDriveService.getInstance().openPicker();
    };

    // Snackbar state
    const [snackbar, setSnackbar] = useState<{
        open: boolean;
        message: string;
        severity: 'success' | 'error' | 'info';
    }>({
        open: false,
        message: '',
        severity: 'info'
    });

    const showNotification = (message: string, severity: 'success' | 'error' | 'info') => {
        setSnackbar({ open: true, message, severity });
        if (snackbarTimerRef.current) clearTimeout(snackbarTimerRef.current);
        snackbarTimerRef.current = setTimeout(() => setSnackbar(prev => ({ ...prev, open: false })), 6000);
    };

    const handleCloseSnackbar = () => {
        setSnackbar(prev => ({ ...prev, open: false }));
    };

    // Reset state on close
    const handleClose = () => {
        if (uploadProgress > 0 && uploadProgress < 100) return; // Prevent closing during upload
        setUploadProgress(0);
        onClose();
    };

    const handleGoogleDriveSelect = async () => {
        try {
            const result = await openPicker();
            if (result) {
                setUploadProgress(1); // Start indeterminate loader

                let documentType: 'text' | 'pdf' | 'word' | 'html' = 'pdf';

                if (result.file.mimeType.includes('pdf')) documentType = 'pdf';
                else if (result.file.mimeType.includes('word') || result.file.mimeType.includes('document')) documentType = 'word';
                else if (result.file.mimeType.includes('text')) documentType = 'text';
                else if (result.file.mimeType.includes('html')) documentType = 'html';

                const resultAction = await dispatch(processDocument.initiate({
                    caseId,
                    knowledgebaseId,
                    documentName: result.file.name,
                    documentType,
                    documentContent: '', // Content streamed backend-side via token
                    googleDriveToken: result.accessToken,
                    googleFileId: result.file.id,
                    metadata: {
                        mimeType: result.file.mimeType,
                        createdAt: new Date().toISOString()
                    },
                }));

                if ('error' in resultAction) {
                    console.error('[GoogleDriveUpload] Upload failed:', resultAction.error);
                    throw new Error('Upload failed');
                }

                showNotification(t('errors.document.uploadedSuccessfully', 'Document uploaded successfully'), 'success');

                dispatch(DBDocumentThunks.find({
                    knowledgebaseId,
                    $sort: { createdAt: -1 }
                }));
                handleClose();
            }
        } catch (error: any) {
            console.error('Google Drive Error:', error);
            if (error?.message !== 'Picker canceled') {
                showNotification(error?.message || 'Failed to open Google Drive picker', 'error');
            }
        } finally {
            setUploadProgress(0);
        }
    };

    const validateFile = (file: File): { valid: boolean; error?: string } => {
        // Size check
        if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
            return { valid: false, error: t('errors.document.sizeExceeds', 'File size exceeds 10MB limit.') };
        }

        // Type check
        const allowedTypes = [
            'application/pdf',
            'application/msword',
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
            'text/plain',
            'text/html'
        ];

        if (!allowedTypes.includes(file.type) && !file.name.endsWith('.docx') && !file.name.endsWith('.pdf')) {
            return { valid: false, error: t('errors.document.unsupportedType', 'Unsupported file type') };
        }

        return { valid: true };
    };

    const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.files && event.target.files.length > 0) {
            await handleUploadDocument(event.target.files[0]);
        }
    };

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(true);
    };

    const handleDragLeave = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(false);
    };

    const handleDrop = async (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(false);
        if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            await handleUploadDocument(e.dataTransfer.files[0]);
        }
    };

    const handleUploadDocument = async (selectedFile: File) => {
        if (!selectedFile) return;

        const validation = validateFile(selectedFile);
        if (!validation.valid) {
            showNotification(validation.error || 'Invalid file', 'error');
            return;
        }

        try {
            setUploadProgress(10); // Start progress
            const reader = new FileReader();

            reader.onload = async (e) => {
                try {
                    const base64Content = e.target?.result?.toString().split(',')[1];
                    if (!base64Content) throw new Error('Failed to read file content');

                    let documentType: 'text' | 'pdf' | 'word' | 'html' = 'pdf';
                    const fileType = selectedFile.type;

                    if (fileType.includes('pdf')) documentType = 'pdf';
                    else if (fileType.includes('word') || fileType.includes('document') || selectedFile.name.endsWith('.docx')) documentType = 'word';
                    else if (fileType.includes('text')) documentType = 'text';
                    else if (fileType.includes('html')) documentType = 'html';

                    setUploadProgress(50); // Creating embeddings...

                    const resultAction = await dispatch(processDocument.initiate({
                        caseId,
                        knowledgebaseId,
                        documentContent: base64Content,
                        documentType,
                        documentName: selectedFile.name,
                        metadata: {
                            mimeType: selectedFile.type,
                            createdAt: new Date().toISOString()
                        },
                    }));

                    if ('error' in resultAction) {
                        throw new Error('Upload failed');
                    }

                    setUploadProgress(100);
                    showNotification(t('errors.document.uploadedSuccessfully', 'Document uploaded successfully'), 'success');
                    dispatch(DBDocumentThunks.find({
                        knowledgebaseId,
                        $sort: { createdAt: -1 }
                    }));
                    handleClose();
                } catch (error: any) {
                    console.error('Failed to process document:', error);
                    showNotification(
                        error.message || error.data?.message || 'Failed to process document. Please try again.',
                        'error'
                    );
                } finally {
                    setUploadProgress(0);
                }
            };

            reader.onerror = () => {
                showNotification(t('errors.document.failedToRead', 'Failed to read file. Please try again.'), 'error');
                setUploadProgress(0);
            };

            reader.readAsDataURL(selectedFile);
        } catch (error: any) {
            console.error('Failed to upload document:', error);
            showNotification(error.message || 'Failed to upload document.', 'error');
            setUploadProgress(0);
        }
    };

    return (
        <>
            <Dialog open={open} onOpenChange={(isOpen) => !isOpen && handleClose()}>
                <DialogContent className="sm:max-w-md" dir={getLanguageDirection(i18n.language)}>
                    <DialogHeader>
                        <DialogTitle>{t('common.knowledgebaseDialogs.uploadDocument')}</DialogTitle>
                        <DialogDescription>
                            {t('common.knowledgebase.selectDocument')}
                        </DialogDescription>
                    </DialogHeader>

                    <Tabs defaultValue="local" className="w-full">
                        <TabsList className="grid w-full grid-cols-2">
                            <TabsTrigger value="local">{t('common.upload.tabs.local', 'Local Upload')}</TabsTrigger>
                            <TabsTrigger value="drive">{t('common.upload.tabs.drive', 'Google Drive')}</TabsTrigger>
                        </TabsList>

                        <TabsContent value="local" className="mt-4">
                            <div
                                onDragOver={handleDragOver}
                                onDragLeave={handleDragLeave}
                                onDrop={handleDrop}
                                onClick={() => document.getElementById('file-upload-input')?.click()}
                                className={cn(
                                    "border-2 border-dashed rounded-lg p-8 text-center transition-all cursor-pointer flex flex-col items-center justify-center gap-4 py-12 relative overflow-hidden group",
                                    isDragging
                                        ? "border-gold bg-gold/10 scale-[1.02]"
                                        : "border-muted-foreground/25 hover:border-gold/50 hover:bg-muted/30"
                                )}
                            >
                                <input
                                    type="file"
                                    id="file-upload-input"
                                    className="hidden"
                                    onChange={handleFileSelect}
                                    accept=".pdf,.doc,.docx,.txt,.html"
                                />
                                <div className={cn(
                                    "p-4 rounded-full transition-colors",
                                    isDragging ? "bg-gold/20" : "bg-gold/10 group-hover:bg-gold/20"
                                )}>
                                    <Upload className="h-8 w-8 text-gold" />
                                </div>
                                <div className="space-y-1">
                                    <p className="font-medium text-sm">
                                        {t('common.upload.local.clickOrDrag', 'Click to upload or drag and drop')}
                                    </p>
                                    <p className="text-xs text-muted-foreground">
                                        {t('common.upload.local.fileLimits', 'PDF, DOCX, TXT up to 10MB')}
                                    </p>
                                </div>
                            </div>
                        </TabsContent>

                        <TabsContent value="drive" className="mt-4">
                            <Card className="border-dashed shadow-none">
                                <CardContent className="flex flex-col items-center justify-center py-12 gap-6">
                                    <GoogleDriveIcon sx={{ fontSize: 64, filter: 'drop-shadow(0 4px 6px rgba(0,0,0,0.1))' }} />
                                    <div className="text-center space-y-1">
                                        <h3 className="font-medium">{t('common.upload.drive.importTitle', 'Import from Google Drive')}</h3>
                                        <p className="text-sm text-muted-foreground max-w-[200px] mx-auto">
                                            {t('common.upload.drive.importDescription', 'Select a file from your Drive to add to the knowledge base.')}
                                        </p>
                                    </div>
                                    <Button
                                        onClick={handleGoogleDriveSelect}
                                        disabled={!isApiLoaded || uploadProgress > 0}
                                        variant="outline"
                                        size="lg"
                                        className="gap-2 min-w-[200px]"
                                    >
                                        {uploadProgress > 0 ? (
                                            <Loader2 className="h-4 w-4 animate-spin" />
                                        ) : (
                                            <GoogleDriveIcon sx={{ fontSize: 18 }} />
                                        )}
                                        {t('common.upload.drive.browse', 'Browse Drive')}
                                    </Button>
                                </CardContent>
                            </Card>
                        </TabsContent>
                    </Tabs>

                    {uploadProgress > 0 && (
                        <div className="mt-4 space-y-2 animate-in fade-in slide-in-from-bottom-2">
                            <div className="flex justify-between text-xs text-muted-foreground">
                                <span>{t('common.upload.status.uploading', 'Uploading...')}</span>
                                <span>{Math.round(uploadProgress)}%</span>
                            </div>
                            <Progress value={uploadProgress} className="h-2" />
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            {/* Toast notification */}
            {snackbar.open && (
                <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[9999] animate-in slide-in-from-bottom-4 fade-in duration-300">
                    <div className={`flex items-center gap-3 px-4 py-3 rounded-xl shadow-lg border text-sm font-medium min-w-[280px] max-w-sm ${
                        snackbar.severity === 'success'
                            ? 'bg-green-700 border-green-600 text-white'
                            : snackbar.severity === 'error'
                            ? 'bg-destructive border-destructive/80 text-white'
                            : 'bg-primary border-primary/80 text-primary-foreground'
                    }`}>
                        {snackbar.severity === 'success' && <CheckCircle size={17} className="shrink-0" />}
                        {snackbar.severity === 'error' && <AlertCircle size={17} className="shrink-0" />}
                        {snackbar.severity === 'info' && <Info size={17} className="shrink-0" />}
                        <span className="flex-1">{snackbar.message}</span>
                        <button onClick={handleCloseSnackbar} className="shrink-0 opacity-80 hover:opacity-100">
                            <X size={15} />
                        </button>
                    </div>
                </div>
            )}
        </>
    );
};

export default DocumentUploadDialog;
